/*! tableOfContents.js v1.0.0 | (c) 2020 Chris Ferdinandi |
MIT License | http://github.com/cferdinandi/table-of-contents */

/*
 * Automatically generate a table of contents from the headings on the page
 * @param  {String} content A selector for the element that the content is in
 * @param  {String} target  The selector for the container to render the table of contents into
 * @param  {Object} options An object of user options [optional]
 *
 * Adjusted to fit SON needs:
 * @param {uuid} for creating unique heading id
 * defaultLevels of headings contain selectors of headings we do no want to show:
 *    - h2:(.invisible) -> invisible headlines should not be included in TOC
 *    - h3:(.descripted-definition-list__headline)
 *    - h3:(.content-headline)
 */
export function generateTableOfContents(uuid, content, target, options) {
  //
  // Variables
  //

  // Get content
  const contentWrap = document.querySelector(content);
  const toc = document.querySelector(target);
  if (!contentWrap || !toc) return;

  // Settings & Defaults
  // settings will be merged from component !
  const defaults = {
    levels: '',
    headingLevel: '',
    listType: '',
  };
  const settings = {};

  // Placeholder for headings
  let headings;

  /**
   * Create an ID for a heading if one does not exist
   * @param  {Node} heading The heading element
   */
  const createID = function (heading, index) {
    if (heading.id.length) return;
    heading.id = `table-of-contents-heading-${uuid}-${index}`;
  };

  /**
   * Merge user options into defaults
   * @param  {Object} obj The user options
   */
  const merge = function (obj) {
    for (const key in defaults) {
      if (Object.prototype.hasOwnProperty.call(defaults, key)) {
        settings[key] = Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : defaults[key];
      }
    }
  };

  /**
   * Get the HTML to indent a list a specific number of levels
   * @param  {Integer} count The number of times to indent the list
   * @return {String}        The HTML
   */
  const getIndent = function (count) {
    let html = '';
    for (let i = 0; i < count; i += 1) {
      html += `<${settings.listType} class="table-of-contents__list">`;
    }
    return html;
  };

  /**
   * Get the HTML to close an indented list a specific number of levels
   * @param  {Integer} count The number of times to "outdent" the list
   * @return {String}        The HTML
   */
  const getOutdent = function (count) {
    let html = '';
    for (let i = 0; i < count; i += 1) {
      html += `</${settings.listType}></li>`;
    }
    return html;
  };

  /**
   * Get the HTML string to start a new list of headings
   * @param  {Integer} diff  The number of levels in or out from the current level the list is
   * @param  {Integer} index The index of the heading in the "headings" NodeList
   * @return {String}        The HTML
   */
  const getStartingHTML = function (diff, index) {
    // If indenting
    if (diff > 0) {
      return getIndent(diff);
    }

    // If outdenting
    if (diff < 0) {
      return getOutdent(Math.abs(diff));
    }

    // If it's not the first item and there's no difference
    if (index && !diff) {
      return '</li>';
    }

    return '';
  };

  /**
   * Inject the table of contents into the DOM
   */
  const injectTOC = function () {
    // Track the current heading level
    let level = headings[0].tagName.slice(1);
    const startingLevel = level;

    // Cache the number of headings
    const len = headings.length - 1;

    // Inject the HTML into the DOM
    toc.innerHTML = `<${settings.listType} class="table-of-contents__list">${Array.prototype.map
      .call(headings, function (heading, index) {
        // Add an ID if one is missing
        createID(heading, index);

        // Check the heading level vs. the current list
        const currentLevel = heading.tagName.slice(1);
        const levelDifference = currentLevel - level;
        level = currentLevel;
        let html = getStartingHTML(levelDifference, index);

        // Generate the HTML
        // we generate the linked heading for the toc with innerText onlay because we don't want its markup in the toc
        html +=
          `<li class="table-of-contents__list-item">` +
          `<a class="table-of-contents__link" href="#${heading.id}">${heading.textContent.trim()}</a>`;

        // If the last item, close it all out
        if (index === len) {
          html += getOutdent(Math.abs(startingLevel - currentLevel));
        }

        return html;
      })
      .join('')}</${settings.listType}>`;
  };

  /**
   * Initialize the script
   */
  const init = function () {
    // Merge any user settings into the defaults
    merge(options || {});

    // Get the headings
    // If none are found, don't render a list
    headings = contentWrap.querySelectorAll(settings.levels);
    if (!headings.length) return;

    // Inject the table of contents
    injectTOC();
  };

  //
  // Initialize the script
  //

  init();
}
